import { matchPath } from 'react-router-dom';
import * as Breakpoints from 'web-app/styleguide/breakpoint-helpers';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import * as NonEmptyArray from 'web-app/util/non-empty-array';
import { withPath } from '@famly/vet_navigation_route-config';
import { hasNavigationLinks, } from './types';
import { getNavigationLinkRouteConfig } from './config/navigation-link';
import { ENABLE_SAFEGUARDING_PAYLOAD_KEY, ENABLE_MEDICATION_FORMS_PAYLOAD_KEY, ENABLE_IMMUNIZATION_PAYLOAD_KEY, ENABLE_HEADCOUNTS_PAYLOAD_KEY, } from './constants';
export const getTabBarHeight = (theme) => Breakpoints.isTabletPortraitAndLarger() && theme.informationArchitectureConfiguration.tabBarHeightTablet
    ? theme.informationArchitectureConfiguration.tabBarHeightTablet
    : theme.informationArchitectureConfiguration.tabBarHeightMobile;
export const doPathsMatch = (currentPath, targetPath) => {
    var _a;
    // https://reactrouter.com/web/api/matchPath
    const match = matchPath(currentPath, {
        path: targetPath,
        exact: true,
        strict: false,
    });
    return (_a = match === null || match === void 0 ? void 0 : match.isExact) !== null && _a !== void 0 ? _a : false;
};
/**
 * Recursively test if `navigationLinkId` is part of the given `navigationLink`. Note that this does not
 * take sub pages into account.
 *
 * @param navigationLinkId
 * @param navigationLink
 */
export const isPartOfNavigationLink = (navigationLinkId, navigationLink) => {
    if (navigationLink.id === navigationLinkId) {
        return true;
    }
    if (navigationLink.subNavigationLinks.length === 0) {
        return false;
    }
    return navigationLink.subNavigationLinks.some(subNavigationLink => isPartOfNavigationLink(navigationLinkId, subNavigationLink));
};
/**
 * Recursively test if a `navigationLinkId` is part of the given `navigationGroup`
 *
 * @param navigationLinkId
 * @param navigationGroup
 * @returns
 */
export const isPartOfNavigationGroup = (navigationLinkId, navigationGroup) => {
    if (!hasNavigationLinks(navigationGroup)) {
        return false;
    }
    return navigationGroup.navigationLinks.some(navigationLink => isPartOfNavigationLink(navigationLinkId, navigationLink));
};
/** Returns the navigation links that are not hidden from the sidebar and mobile menu. */
export const getVisibleNavigationLinks = (links) => links.filter(link => !link.hidden);
/**
 * Recursively test if a `navigationLinkId` is a sub page of the given `navigationLink`
 */
export const isSubPageOfNavigationLink = (navigationLinkId, navigationLink) => {
    var _a;
    if ((_a = navigationLink.subPages) === null || _a === void 0 ? void 0 : _a.some(subNavigationLinkId => subNavigationLinkId === navigationLinkId)) {
        return true;
    }
    if (navigationLink.subNavigationLinks.length === 0) {
        return false;
    }
    return navigationLink.subNavigationLinks.some(subNavigationLink => isSubPageOfNavigationLink(navigationLinkId, subNavigationLink));
};
/**
 * Recursively test if a `subNavigationLinkId` is a sub page of the given `navigationLink`
 */
export const isSublinkOfNavigationLink = (subNavigationLinkId, navigationLink) => {
    var _a, _b;
    return ((_b = (_a = navigationLink.subNavigationLinks) === null || _a === void 0 ? void 0 : _a.some(subNavigationLink => subNavigationLink.id === subNavigationLinkId)) !== null && _b !== void 0 ? _b : false);
};
/**
 * Determine whether the given `navigationLink` has sub navigation links
 */
export const hasSubNavigationLinks = (navigationLink) => {
    return navigationLink.subNavigationLinks.length > 0;
};
/**
 * Whether the sublinks of `navigationLink` are shown as topbar tabs, rather than as an expandable sidebar row.
 * `isTabsInTopbarEnabled` is the navigation-wide setting; a link can opt in on its own.
 */
export const areSublinksShownAsTabs = (navigationLink, isTabsInTopbarEnabled) => isTabsInTopbarEnabled || Boolean(navigationLink === null || navigationLink === void 0 ? void 0 : navigationLink.showSublinksAsTabs);
export const noAccessRoute = withPath('/account/institution/:institutionId/noAccess');
/**
 * Creates path (e.g. `/account/institution/...`) and path params (containing the necessary entity id and possible
 * query parameters necessary to build the URL) for the given navigation link based on the given entity. If the given
 * entity is an institution and doesn't have access to the navigation link it returns the URL for the no-acccess,
 * otherwise it returns null.
 *
 * @param navigationLink The navigation link to create the URL for
 * @param entity The entity to generate create the URL for
 * @param config An optional configuration object with the possibility to force the function to create entity specific urls
 */
export const createUrlForEntity = (navigationLink, entity, config = { ignoreTopLevelRoute: false }) => {
    const routeConfig = getNavigationLinkRouteConfig(navigationLink.id);
    if (routeConfig.topLevelRoute && !config.ignoreTopLevelRoute) {
        return {
            path: routeConfig.topLevelRoute.path,
        };
    }
    switch (entity.type) {
        case 'custom-entity': {
            if (!routeConfig.customEntityRoutes) {
                return null;
            }
            const targetRouteConfig = routeConfig.customEntityRoutes.find(customEntityRoute => {
                return customEntityRoute.id === entity.id;
            });
            if (!targetRouteConfig) {
                return null;
            }
            return {
                path: targetRouteConfig.path,
            };
        }
        case 'organization': {
            const hasAccess = navigationLink.organizations.some(organization => organization.organizationId === entity.id);
            if (!hasAccess || !routeConfig.organizationRoute) {
                return null;
            }
            return {
                path: routeConfig.organizationRoute.path,
                pathParams: { organizationId: entity.id },
            };
        }
        case 'institution': {
            // A link that carries no institutions at all (e.g. an organization-only settings page) isn't meant to be
            // reached through an institution entity in the first place — return null so the caller falls back to
            // resolving via the link's own organizations, rather than reporting a false no-access result for every
            // institution-scoped user.
            if (navigationLink.institutions.length === 0) {
                return null;
            }
            const hasAccess = institutionHasAccessToNavigationLink(entity.id, navigationLink);
            return hasAccess && routeConfig.institutionRoute
                ? {
                    path: routeConfig.institutionRoute.path,
                    pathParams: {
                        institutionId: entity.id,
                    },
                }
                : {
                    path: noAccessRoute,
                    pathParams: {
                        institutionId: entity.id,
                        queryParams: {
                            navigationLinkId: `${navigationLink.id}`,
                        },
                    },
                };
        }
        default:
            return null;
    }
};
const safeGuardingNavigationLinkIds = [
    'ACCIDENTS_INCIDENTS',
    'MEDICATION_FORMS',
    'HEAD_COUNT',
    'IMMUNIZATION',
];
const payloadKeysMap = {
    ['ACCIDENTS_INCIDENTS']: ENABLE_SAFEGUARDING_PAYLOAD_KEY,
    ['MEDICATION_FORMS']: ENABLE_MEDICATION_FORMS_PAYLOAD_KEY,
    ['IMMUNIZATION']: ENABLE_IMMUNIZATION_PAYLOAD_KEY,
    ['HEAD_COUNT']: ENABLE_HEADCOUNTS_PAYLOAD_KEY,
};
/**
 * Checks if the institution with the given institution id has given to a navigation link
 *
 * This includes some special handling of the safeguarding links as their behaviors are
 * structured differently from all of the other navigation links.
 */
export const institutionHasAccessToNavigationLink = (institutionId, navigationLink) => {
    var _a;
    if (safeGuardingNavigationLinkIds.includes(navigationLink.id)) {
        const payloadForInstitution = (_a = navigationLink.institutions.find(institution => institution.institutionId === institutionId)) === null || _a === void 0 ? void 0 : _a.payload;
        if (!payloadForInstitution) {
            return false;
        }
        const payloadKey = payloadKeysMap[navigationLink.id];
        return payloadForInstitution[payloadKey] === true;
    }
    return navigationLink.institutions.some(institution => institution.institutionId === institutionId);
};
/**
 * Determines if the given NavigationLink is valid
 */
const isNavigationLinkValid = (navigationLink) => {
    var _a;
    // Some NavigationLinks don't have any entities attached to them but are still valid for parents
    if (parentSpecificNavigationLinkIds.includes(navigationLink.id)) {
        return true;
    }
    const customEntities = (_a = navigationLink.customEntities) !== null && _a !== void 0 ? _a : [];
    return (navigationLink.institutions.length > 0 || navigationLink.organizations.length > 0 || customEntities.length > 0);
};
const parentSpecificNavigationLinkIds = [
    'BILL_PAYER',
    'BILL_PAYERS',
    'LESSON_PLANS_PARENT',
];
/**
 * Creates a NavigationLink based on the passed configuration
 */
export const createNavigationLink = (config) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const subNavigationLinks = ((_a = config.subNavigationLinks) !== null && _a !== void 0 ? _a : []).filter(hasValue).filter(isNavigationLinkValid);
    return {
        id: config.id,
        institutions: (_c = (_b = config.entities) === null || _b === void 0 ? void 0 : _b.institutions) !== null && _c !== void 0 ? _c : [],
        organizations: (_e = (_d = config.entities) === null || _d === void 0 ? void 0 : _d.organizations) !== null && _e !== void 0 ? _e : [],
        customEntities: (_f = config.entities) === null || _f === void 0 ? void 0 : _f.custom,
        subNavigationLinks,
        ...(config.showSublinksAsTabs ? { showSublinksAsTabs: true } : {}),
        subPages: (_g = config.subPages) !== null && _g !== void 0 ? _g : [],
        isRemovable: (_h = config.isRemovable) !== null && _h !== void 0 ? _h : false,
        payload: config.payload,
    };
};
/**
 * Creates a NavigationGroup based on the passed configuration
 *
 * @returns the created NavigationGroup or null if none of the passed `navigationLinks` are valid
 */
export const createNavigationGroupWithNavigationLinks = (config) => {
    const navigationLinks = config.navigationLinks.filter(hasValue).filter(isNavigationLinkValid);
    if (!NonEmptyArray.isNonEmpty(navigationLinks)) {
        return null;
    }
    return {
        type: 'WithNavigationLinks',
        id: config.id,
        navigationLinks,
        hideNavigationLinks: config.hideNavigationLinks,
        flattenOnMobile: config.flattenOnMobile,
    };
};
